import { Component, ElementRef, ViewChild, computed, Input, TemplateRef, inject, Signal, OnInit } from '@angular/core';
import { MediaPlayerFile, MediaTranscodingStatus } from '../../../../interfaces/media-player-store.interface';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../../services/media-player-store.service';
import { MediaType, FileTranscodingStatus } from '../../../../models/media-player-types';
import { FailedTranscodingTypes } from '../pdf-transcode-error/pdf-transcode-error.component';

@Component({
  selector: 'image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrl: './image-viewer.component.scss',
  standalone: false,
})
export class ImageViewerComponent implements OnInit {
  @Input() instanceId: string;
  @Input() assetListTemplate!: TemplateRef<any>;
  @Input() assetInfoTemplate!: TemplateRef<any>;
  @Input() mode: string | null = null;
  @Input() isKpfPreview: boolean = false;

  @ViewChild('imageContainer', { static: true }) imageContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('imageElement', { static: true }) imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild('canvasContainer', { static: true }) canvasContainer!: ElementRef<HTMLDivElement>;

  // Expose state service for template
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  readonly MediaType = MediaType;
  readonly FileTranscodingStatus = FileTranscodingStatus;
  readonly TranscodingTypes: typeof FailedTranscodingTypes = FailedTranscodingTypes;
  mpStore: MediaPlayerStore | null = null;

  // Direct signals from mpStore
  readonly hasEditPermissionSignal: Signal<boolean> = computed(() => this.mpStore?.hasEditPermissionSignal() ?? false);
  readonly annotationsSignal: Signal<any[]> = computed(() => this.mpStore?.annotationsSignal() ?? []);
  readonly annotationsLoadedSignal: Signal<boolean> = computed(() => this.mpStore?.annotationsLoadedSignal() ?? false);
  readonly portfolioPreviewSignal: Signal<boolean> = computed(() => this.mpStore?.portfolioPreviewSignal() ?? false);
  readonly enableResponsiveSignal: Signal<boolean> = computed(() => this.mpStore?.enableResponsiveSignal() ?? false);
  readonly enableAnnotationSignal: Signal<boolean> = computed(() => this.mpStore?.enableAnnotationSignal() ?? false);
  readonly isShareLinkPreviewSignal: Signal<boolean> = computed(
    () => this.mpStore?.isShareLinkPreviewSignal() ?? false
  );
  readonly isReleasePreviewSignal: Signal<boolean> = computed(() => this.mpStore?.isReleasePreviewSignal() ?? false);
  readonly parentElementContainerSignal: Signal<string | null> = computed(
    () => this.mpStore?.parentElementContainerSignal() ?? null
  );
  readonly mediaTypeSignal: Signal<string | null> = computed(() => this.mpStore?.mediaTypeSignal() ?? null);
  readonly projectIdSignal: Signal<string | null> = computed(() => this.mpStore?.projectIdSignal() ?? null);
  readonly sectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.sectionIdSignal() ?? null);
  readonly subsectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.subsectionIdSignal() ?? null);
  readonly typeIdSignal: Signal<string | null> = computed(() => this.mpStore?.typeIdSignal() ?? null);
  readonly projectParticipantsSignal: Signal<any[]> = computed(() => this.mpStore?.projectParticipantsSignal() ?? []);
  readonly subsectionStateSignal: Signal<any> = computed(() => this.mpStore?.subsectionStateSignal() ?? null);
  readonly imgSrcSignal: Signal<any> = computed(() => this.mpStore?.imgSrcSignal() ?? null);
  readonly itemSignal: Signal<MediaPlayerFile | null> = computed(() => this.mpStore?.itemSignal() ?? null);
  readonly userInfoSignal: Signal<any> = computed(() => this.mpStore?.userInfoSignal() ?? null);
  readonly instanceIdSignal: Signal<string> = computed(() => this.mpStore?.instanceIdSignal() ?? '');
  readonly hideAnnotationToolbarSignal: Signal<boolean> = computed(
    () => this.mpStore?.hideAnnotationToolbarSignal() ?? false
  );
  readonly isLoadingSignal: Signal<boolean> = computed(() => this.mpStore?.isLoadingSignal() ?? false);
  readonly fileTranscodingStatusSignal: Signal<MediaTranscodingStatus> = computed(
    () => this.mpStore?.fileTranscodingStatusSignal() ?? null
  );

  ngOnInit(): void {
    this.mpStore = this.mpStoreService.getStore(this.instanceId);
    if (!this.mpStore) {
      throw new Error(`Failed to get media player store for instanceId: ${this.instanceId}`);
    }
  }

  public showImageInNewTab(): void {
    const item: MediaPlayerFile | null = this.mpStore.itemSignal() ?? null;
    const url: string | undefined = item?.signedURL || item?.publicLink || undefined;
    if (!url) {
      return;
    }
    globalThis.open(url, '_blank', 'noopener,noreferrer');
  }
}
